import { animate, style, transition, trigger } from '@angular/animations';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ReceiptStep } from '@app/shared/enums/common.enums';
import { DropoffService } from '@app/shared/services/drop-off.service';
import { PPUAlertService } from '@app/shared/services/ppu-alert.service';
import { TranslateService } from '@ngx-translate/core';
import { delay, of, Subject, takeUntil, timer } from 'rxjs';
import { EMAIL_PATTERN } from '@app/shared/validators/email.validator';
import { PHONE_PATTERN } from '@app/shared/validators/phone.validator';

@Component({
  selector: 'app-send-receipt-popup',
  templateUrl: './send-receipt-popup.component.html',
  standalone: false,
  animations: [
    trigger('fadeAnimation', [
      // When content enters the DOM
      transition(':enter', [style({ opacity: 0 }), animate('300ms ease-out', style({ opacity: 1 }))]),
    ]),
  ],
})
export class SendReceiptPopupComponent implements OnInit {
  step: ReceiptStep = ReceiptStep.Handoff;
  receiptForm!: FormGroup;
  private destroy$ = new Subject<void>();
  hasPickPack: boolean = false;

  constructor(
    private dialogRef: MatDialogRef<SendReceiptPopupComponent>,
    private fb: FormBuilder,
    private dropoffService: DropoffService,
    private translateService: TranslateService,
    private ppuAlertService: PPUAlertService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      packageIds: number[];
      trackingNumbers: string[];
      storeName: string;
      carriers: string[];
    }
  ) {
    this.dialogRef.updateSize('490px', 'auto');
    this.hasPickPack = data.carriers.some(x => x == "PickPack");
    this.receiptForm = this.fb.group({
        method: [this.hasPickPack  ? "email" : "sms"],
        phone: ['', Validators.pattern(PHONE_PATTERN)],
        email: ['', [Validators.email, Validators.pattern(EMAIL_PATTERN)]],
        optIn: [true]
    });
  }

  ngOnInit(): void {
    // auto-advance from handoff to form
    timer(6000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => (this.step = ReceiptStep.Form));
  }

  allowOnlyNumberAndHyphen(event: KeyboardEvent): void {
    const allowedChars = /[0-9\-]/;
    if (!allowedChars.test(event.key)) {
      event.preventDefault();
    }
  }

  send(): void {
    if (this.receiptForm.invalid) {
      return;
    }

    const { email, phone, optIn } = this.receiptForm.value;

    this.dropoffService
      .sendReceipt(this.data.packageIds, email ?? '', phone ?? '', optIn)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.step = ReceiptStep.ThankYou; // show green check
          // keep checkmark visible 6s, then close
          timer(6000)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.dialogRef.close(true));
        },
        error: () => {
          this.ppuAlertService.errorEvent(this.translateService.instant('App.ReceiptPopup.ErrorMsg'));
        },
      });
  }

  close(sent = false): void {
    this.dialogRef.close(sent);
  }

  // select radio of input in focus
  public selectMethod(channel: 'sms' | 'email'): void {
    this.receiptForm.get('method')!.setValue(channel, { emitEvent: false });
  }

  get pkgWord() {
    return this.data.trackingNumbers.length === 1
      ? 'App.ReceiptPopup.Package.Singular'
      : 'App.ReceiptPopup.Package.Plural';
  }

  get trackingList(): string {
    return this.data.trackingNumbers.map((num) => `#${num}`).join(', ');
  }

    get carrierLabel(): string {
        const unique = [...new Set(this.data.carriers.filter(Boolean))];
      
        if (unique.length === 0) {
          return '';
        }
        if (unique.length === 1) {
          return `${unique[0]} `;
        }
        // 2+ carriers → join with comma + "and"
        const last = unique.pop();
        return `${unique.join(', ')} and ${last} `;
    }

}
